import {type UserState} from '../../../../../shared/UserState.ts'
import {StoreConsumable, StoreItemCategory} from '../../../../../shared/entity_datas/StoreEnums.ts'
import {StoreItemController} from '../../../../../shared/store/StoreItemController.ts'
import {createDivIn, createImgIn, createParagraphIn, createSpanIn, setElementInnerHTML, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {ViewPopupInput} from '../../ui_popup/ViewPopupInput.ts'
import {PlayerEnums} from '../../../../../shared/GameEnums.ts'
import PlayerConstants = PlayerEnums.PlayerConstants
import {LobbyImage} from '@colonist/types'
import {type TranslationObject} from '@colonist/i18n'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables.ts'
import {ViewPopupLogin} from '../../ui_popup/ui_popup_membership_incentive/ViewPopupLogin.ts'
import {UIPopupController} from '../../ui_popup/UIPopupController.ts'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints.ts'
import {type AjaxDataTypes} from '../../../../../shared/AjaxDataTypes.ts'
import {clientI18n} from '../../ClientI18n.ts'
import {SharedUsernameValidator} from '../../../../../shared/SharedUsernameValidator.ts'
import {navigateToPage, throttleUserAction} from '../../ui_utils/ClientUtils.ts'
import {ClientUserState} from '../../ClientUserState.ts'

class UIProfileUsernameControllerState {
    username: string
    usernameChangeAttemptsLeft: number
    isGuest: boolean
    colonistCoins: number
}

enum UsernameValidationState {
    IsSame,
    NonAlphanumeric,
    InvalidLength,
    HasBadWords,
    IsTaken,
    Valid,
}

export abstract class UIProfileUsernameController extends ViewPopupInput {
    protected state: UIProfileUsernameControllerState = {
        username: '',
        usernameChangeAttemptsLeft: 0,
        isGuest: false,
        colonistCoins: 0,
    }
    private readonly usernameValidationMessage: HTMLSpanElement
    private readonly suggestedUsernamesContainer: HTMLDivElement
    private readonly usernameChangeAttemptsLeftContainer: HTMLParagraphElement
    private usernameValidationState: UsernameValidationState = UsernameValidationState.IsSame
    private suggestedPostfixes: string[]

    constructor(title?: TranslationObject, body?: TranslationObject, xAction?: () => void) {
        const context = UIUtilsEjsVariables.isUserOnDiscord ? 'discord' : ''
        const popupTitle = title ?? {key: 'strings:homePage.header.changeUsername.title', options: {context: context}}
        const popupBody = body ?? {key: 'strings:homePage.header.changeUsername.body', options: {context: context}}

        super(popupTitle, popupBody, (username) => {
            void this.submit(username)
        }, xAction ?? (() => undefined), true)

        const usernameValidationMessageContainer = createDivIn(this.form, '')
        this.usernameValidationMessage = createSpanIn(usernameValidationMessageContainer, 'popup-footnote warning-text', {key: 'strings:colonistCommon.utils.emptyString'})
        this.suggestedUsernamesContainer = createDivIn(usernameValidationMessageContainer, 'popup-footnote suggestions-wrapper')
        this.usernameChangeAttemptsLeftContainer = createParagraphIn(this.form, {key: 'strings:colonistCommon.utils.emptyString'}, 'popup-footnote')

        this.setFocusStyles(this.input)

        void this.initializeValidityCheck()

        ClientUserState.registerChangeListener((userState) => {
            if(userState == undefined) return
            this.setState(userState)
        })
    }

    private static get usernameChangePrice(): number {
        const usernameChangeStoreItem = StoreItemController.getItemDetail(StoreItemCategory.Consumable, StoreConsumable.UsernameChange)
        return usernameChangeStoreItem?.price ?? 0
    }

    private static createUsernameChangePriceElementIn(container: HTMLElement) {
        const usernameChangeCost = UIProfileUsernameController.usernameChangePrice
        const colonistCoinsImageElement = createImgIn(container, LobbyImage.ColonistCoin)
        colonistCoinsImageElement.style.width = '1.25em'
        colonistCoinsImageElement.style.marginRight = '5px'
        createSpanIn(container, '', {key: 'strings:colonistCommon.utils.#buffer', options: {value: usernameChangeCost}})
    }

    private setState(userState: UserState) {
        this.state = {
            username: userState.username,
            usernameChangeAttemptsLeft: userState.usernameChangeAttemptsLeft,
            isGuest: !userState.isLoggedIn,
            colonistCoins: userState.items.colonistCoins,
        }
        this.updateUsernameLengthLimits()
        this.setCharacterCounter()
        this.updateUsernameInputText()
        this.updateChangeAttemptsLeftContainer()
        this.updateSubmitButtonContent()
    }

    private async setVisible(visible: boolean) {
        if(visible) {
            this.updateUsernameInputText()
            this.updateCharacterCounter()
            await this.updateUsernameValidityElements()

            this.input.classList.remove('invalid-text')
            this.usernameValidationMessage.style.visibility = 'hidden'

            if(this.state.isGuest && this.state.usernameChangeAttemptsLeft <= 0) {
                UIProfileUsernameController.showOutOfAttemptsAsGuestPopup()
                return
            }
        }
        this.container.style.display = `${visible ? 'block' : 'none'}`

        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return
        this.input.focus()
    }

    override show() {
        void this.setVisible(true)
    }

    override removePopup() {
        void this.setVisible(false)
    }

    private updateUsernameInputText() {
        this.input.value = this.state.username
    }

    private updateUsernameLengthLimits() {
        const maxLength = this.state.isGuest ? PlayerConstants.MAX_GUEST_USERNAME_LENGTH : PlayerConstants.MAX_USERNAME_LENGTH
        this.setLengthLimits(PlayerConstants.MIN_USERNAME_LENGTH, maxLength)
    }

    private updateChangeAttemptsLeftContainer() {
        if(this.state.usernameChangeAttemptsLeft == 0) {
            this.usernameChangeAttemptsLeftContainer.style.display = 'none'
            return
        }
        this.usernameChangeAttemptsLeftContainer.style.display = 'block'
        setElementInnerHTML(this.usernameChangeAttemptsLeftContainer, {
            key: 'strings:homePage.header.changeUsername.usernameChangeAttemptsLeftNote',
            options: {count: this.state.usernameChangeAttemptsLeft},
        })
    }

    private updateSubmitButtonContent() {
        setElementInnerHTML(this.checkButton, {key: 'strings:colonistCommon.utils.emptyString'})
        if(this.state.usernameChangeAttemptsLeft <= 0) {
            this.checkButton.classList.remove('btn_general_check')
            this.showCostInSubmitButton()
        } else {
            this.checkButton.classList.add('btn_general_check')
        }
    }

    private showCostInSubmitButton() {
        UIProfileUsernameController.createUsernameChangePriceElementIn(this.checkButton)
    }

    private async submit(username: string) {
        await this.checkUsernameValidity()
        if(this.usernameValidationState != UsernameValidationState.Valid) return

        const canChangeUsernameForFree = this.state.usernameChangeAttemptsLeft > 0
        if(canChangeUsernameForFree) {
            this.showConfirmUsernameChangeWithAttemptsLeftPopup(username)
            return
        }

        const haveEnoughColonistCoins = this.state.colonistCoins >= UIProfileUsernameController.usernameChangePrice
        if(haveEnoughColonistCoins) {
            this.showConfirmUsernameChangeWithCoinsPopup(username)
            return
        }

        if(window.location.pathname == '/store') {
            this.showNotEnoughCoinsToChangeUsernamePopup(() => {
                const coinSection = document.getElementById('coins')
                coinSection?.scrollIntoView({behavior: 'smooth'})
            })
            return
        }

        this.showNotEnoughCoinsToChangeUsernamePopup(() => {
            navigateToPage('/store#coins', true, true)
        })
    }

    private static showOutOfAttemptsAsGuestPopup() {
        new ViewPopupLogin()
    }

    protected showConfirmUsernameChangeWithAttemptsLeftPopup(username: string) {
        const body: TranslationObject = {
            key: 'strings:homePage.header.changeUsername.confirm.body',
            options: {count: this.state.usernameChangeAttemptsLeft - 1},
        }

        const confirmAction = () => {
            void this.setVisible(false)
            void this.updateUsername(username, false)
        }

        const cancelAction = () => undefined

        UIPopupController.create({key: 'strings:homePage.header.changeUsername.confirm.title'},
            body,
            confirmAction,
            cancelAction)
    }

    private showNotEnoughCoinsToChangeUsernamePopup(successAction: () => void) {
        UIPopupController.create({key: 'strings:homePage.header.changeUsername.notEnoughCoins.title'},
            {key: 'strings:homePage.header.changeUsername.notEnoughCoins.body'},
            () => {
                void this.setVisible(false)
                successAction()
            },
            () => undefined,
            true,
            {
                showXButton: false,
                checkButtonCustomContent: {key: 'strings:homePage.header.changeUsername.notEnoughCoins.checkButton'},
            })
    }

    private showConfirmUsernameChangeWithCoinsPopup(username: string) {
        const priceElement = document.createElement('span') as HTMLSpanElement
        UIProfileUsernameController.createUsernameChangePriceElementIn(priceElement)

        const confirmAction = () => {
            void this.setVisible(false)
            void this.updateUsername(username, true)
        }

        const cancelAction = () => undefined

        const body: TranslationObject = {
            key: 'strings:homePage.header.changeUsername.confirm.body',
            options: {
                context: 'coins',
                interpolation: {escapeValue: false},
                price: priceElement.outerHTML,
            },
        }

        UIPopupController.create({key: 'strings:homePage.header.changeUsername.confirm.title'},
            body,
            confirmAction,
            cancelAction)
    }

    abstract updateUsername(newUsername: string, shouldPurchaseIfNoAttemptsLeft: boolean)

    private initializeValidityCheck() {
        void this.updateUsernameValidityElements()
        this.input.addEventListener('input', () => {
            throttleUserAction(async() => void this.updateUsernameValidityElements())()
        })
    }

    private async updateUsernameValidityElements() {
        this.disableSubmission()
        await this.checkUsernameValidity()
        this.updateCustomValidityAndErrorMessage()
        this.updateUsernameTextColor()
        this.updateSuggestedUsernames()
        this.updateCheckButtonState()
    }

    private disableSubmission() {
        const errorMessage: TranslationObject = {key: 'strings:homePage.header.changeUsername.validationMessage.pleaseWait'}
        const customValidityErrorMessage = clientI18n.t(errorMessage.key)
        this.input.setCustomValidity(customValidityErrorMessage)
        this.checkButton.style.pointerEvents = 'none'
    }

    private async checkUsernameValidity() {
        const usernameToValidate = this.input.value
        this.suggestedPostfixes = []

        if(usernameToValidate.toLowerCase() == this.state.username.toLowerCase()) {
            this.usernameValidationState = UsernameValidationState.IsSame
            return
        }

        if(!SharedUsernameValidator.containsOnlyAlphanumeric(usernameToValidate)) {
            this.usernameValidationState = UsernameValidationState.NonAlphanumeric
            return
        }

        if(!SharedUsernameValidator.isValidLength(!this.state.isGuest, usernameToValidate)) {
            this.usernameValidationState = UsernameValidationState.InvalidLength
            return
        }

        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiValidateUsername(usernameToValidate), undefined, undefined)

        if(usernameToValidate != this.input.value) return

        const serverValidation = response?.json as AjaxDataTypes.UsernameValidationData
        if(serverValidation == undefined) return

        if(serverValidation.containsToxicWord) {
            this.usernameValidationState = UsernameValidationState.HasBadWords
            return
        }

        if(!serverValidation.isAvailable) {
            this.usernameValidationState = UsernameValidationState.IsTaken
            this.suggestedPostfixes = serverValidation.suggestedPostfixes
            return
        }

        this.usernameValidationState = UsernameValidationState.Valid
    }

    private updateCustomValidityAndErrorMessage() {
        let errorMessage: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}

        switch(this.usernameValidationState) {
            case UsernameValidationState.Valid:
                this.usernameValidationMessage.style.visibility = 'hidden'
                this.input.setCustomValidity('')
                return
            case UsernameValidationState.IsSame:
                errorMessage = {key: 'strings:homePage.header.changeUsername.validationMessage.notDifferent'}
                break
            case UsernameValidationState.InvalidLength:
                errorMessage = {
                    key: 'strings:homePage.header.changeUsername.validationMessage.invalidLength',
                    options: {minLength: PlayerConstants.MIN_USERNAME_LENGTH, maxLength: PlayerConstants.MAX_USERNAME_LENGTH},
                }
                break
            case UsernameValidationState.NonAlphanumeric:
                errorMessage = {key: 'strings:homePage.header.changeUsername.validationMessage.nonAlphanumeric'}
                break
            case UsernameValidationState.HasBadWords:
                errorMessage = {key: 'strings:homePage.header.changeUsername.validationMessage.hasBadWords'}
                break
            case UsernameValidationState.IsTaken:
                errorMessage = {key: 'strings:homePage.header.changeUsername.validationMessage.notAvailable'}
                break
        }
        setElementInnerHTML(this.usernameValidationMessage, errorMessage)
        this.usernameValidationMessage.style.visibility = 'visible'

        const customErrorMessage = UIUtilsEjsVariables.isUserOnDiscordMobile ? ' ' : clientI18n.t(errorMessage.key, errorMessage.options)
        this.input.setCustomValidity(customErrorMessage)
    }

    private updateUsernameTextColor() {
        if(this.usernameValidationState == UsernameValidationState.Valid) {
            this.input.classList.remove('invalid-text')
            return
        }
        this.input.classList.add('invalid-text')
    }

    private updateSuggestedUsernames() {
        setElementInnerHTML(this.suggestedUsernamesContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.suggestedPostfixes?.forEach((postfix) => {
            const suggestedUsername = this.input.value + postfix
            const suggestionElement = createSpanIn(this.suggestedUsernamesContainer, 'suggestion-text', {key: 'strings:colonistCommon.utils.#buffer', options: {value: suggestedUsername}})
            setElementOnClick(suggestionElement, () => {
                this.input.value = suggestedUsername
                this.updateCharacterCounter()
                void this.updateUsernameValidityElements()
            })
        })
    }
}
